import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "../../../../components/MDButton";
import FormField from "layouts/ecommerce/products/new-product/components/FormField";

import amenitiesService from "../../../../services/amenities.service";

function AmenityQuestions({ validStep, oldValues, valueSetter }) {
  const [allQuestions, setAllQuestions] = useState([]);
  const [selectedExisting, setSelectedExisting] = useState([]);
  const [customQuestions, setCustomQuestions] = useState([]);
  const [formValue, setFormValue] = useState("");
  const [correctValue, setCorrectValue] = useState("yes");
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [questionError, setQuestionError] = useState("");

  useEffect(() => {
    validStep(true);

    amenitiesService
      .getQuestions()
      .then((res) => {

        console.log("QUESTIONS API RESPONSE:", res.data);

        setAllQuestions(res.data || []);

      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {

    if (!oldValues || !oldValues.questions || allQuestions.length === 0)
      return;

    const existing = oldValues.questions
      .map(q =>
        allQuestions.find(aq => aq.id === q.id)
      )
      .filter(Boolean);

    setSelectedExisting(existing);

  }, [oldValues, allQuestions]);

  const buildQuestionsPayload = () => {
    return [
      ...selectedExisting.map((q) => ({ id: q.id })),
      ...customQuestions.map((q) => ({
        text: q.text,
        correct_answer: q.correct_answer,
      })),
    ];
  };
  const handleAddCustomQuestion = () => {
    const text = formValue.trim();
    setQuestionError("");

    if (!text) {
      setQuestionError("Question text is required.");
      return;
    }

    const existsInCustom = customQuestions.some(
      (q) => q.text?.trim().toLowerCase() === text.toLowerCase()
    );

    const existsInExisting = allQuestions.some(
      (q) => q.question_text?.trim().toLowerCase() === text.toLowerCase()
    );

    if (existsInCustom || existsInExisting) {
      setQuestionError("This question already exists.");
      return;
    }

    setCustomQuestions((prev) => [
      ...prev,
      {
        text,
        correct_answer: (correctValue || "yes").toLowerCase(),
      },
    ]);

    setFormValue("");
    setCorrectValue("yes");
    setQuestionError("");

    valueSetter((prev) => ({
      ...prev,
      questions: [
        ...selectedExisting.map((q) => ({ id: q.id })),
        ...[
          ...customQuestions,
          {
            text,
            correct_answer: correctValue.toLowerCase(),
          },
        ].map((q) => ({
          text: q.text,
          correct_answer: q.correct_answer,
        })),
      ],
    }));
  };

  const removeExistingQuestion = (id) => {

    const updated = selectedExisting.filter(q => q.id !== id);

    setSelectedExisting(updated);

    valueSetter((prev) => ({
      ...prev,
      questions: [
        ...updated.map(q => ({ id: q.id })),
        ...customQuestions.map(q => ({
          text: q.text,
          correct_answer: q.correct_answer
        }))
      ]
    }));
  };

  const removeCustomQuestion = (index) => {
    setCustomQuestions((prev) => {
      const updated = prev.filter((_, i) => i !== index);

      valueSetter((previousValues) => ({
        ...previousValues,
        questions: [
          ...selectedExisting.map((q) => ({ id: q.id })),
          ...updated.map((q) => ({
            text: q.text,
            correct_answer: q.correct_answer,
          })),
        ],
      }));

      return updated;
    });
  };

  return (
    <MDBox>
      <MDTypography variant="h5">Amenity Questions</MDTypography>
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h5">Questions</MDTypography>

        <IconButton
          onClick={() => setShowAddQuestion(!showAddQuestion)}
        >
          <Icon>add</Icon>
        </IconButton>
      </MDBox>
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={allQuestions}
              value={selectedExisting}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.question_text || ""}
              onChange={(event, value) => {

                setSelectedExisting(value || []);

                valueSetter((prev) => ({
                  ...prev,
                  questions: [
                    ...(value || []).map((q) => ({ id: q.id })),
                    ...customQuestions.map((q) => ({
                      text: q.text,
                      correct_answer: q.correct_answer,
                    })),
                  ],
                }));

              }}
              renderInput={(params) => (
                <MDInput {...params} label="Select Existing Questions" />
              )}
            />
          </Grid>
          {showAddQuestion && (
            <>

              <Grid item xs={8}>
                <MDInput
                  fullWidth
                  multiline
                  minRows={3}
                  label="New Question"
                  value={formValue}
                  error={Boolean(questionError)}
                  onChange={(e) => {
                    setFormValue(e.target.value);
                    setQuestionError("");
                  }}
                />
                {questionError && (
                  <MDTypography variant="caption" color="error">
                    {questionError}
                  </MDTypography>
                )}
              </Grid>

              <Grid item xs={4}>
                <Autocomplete
                  options={["yes", "no"]}
                  value={correctValue}
                  onChange={(e, v) => setCorrectValue(v || "yes")}
                  renderInput={(params) => (
                    <FormField
                      {...params}
                      InputLabelProps={{ shrink: true }}
                      label="Select Response"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <MDBox display="flex" gap={1}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    size="small"
                    onClick={handleAddCustomQuestion}
                  >
                    add
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="light"
                    size="small"
                    onClick={() => {
                      setFormValue("");
                      setCorrectValue("yes");
                      setQuestionError("");
                    }}
                  >
                    cancel
                  </MDButton>
                </MDBox>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            {selectedExisting.map((q) => (
              <MDBox
                key={`existing-${q.id}`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                bgColor="white"
                borderRadius="xl"
                mt={2}
                py={1.5}
                px={2}
              >
                <span>{q.question_text}</span>
                <IconButton onClick={() => removeExistingQuestion(q.id)}>
                  <Icon color="error">delete</Icon>
                </IconButton>
              </MDBox>
            ))}

            {customQuestions.map((q, index) => (
              <MDBox
                key={`custom-${index}`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                bgColor="white"
                borderRadius="xl"
                mt={2}
                py={1.5}
                px={2}
              >
                <span>
                  {q.text} ({q.correct_answer})
                </span>
                <IconButton onClick={() => removeCustomQuestion(index)}>
                  <Icon color="error">delete</Icon>
                </IconButton>
              </MDBox>
            ))}
          </Grid>
        </Grid>
      </MDBox>
    </MDBox>
  );
}

export default AmenityQuestions;
